
import format from 'date-fns/format';
import subMonths from 'date-fns/subMonths';
import addMonths from 'date-fns/addMonths';
import getDaysInMonth from 'date-fns/getDaysInMonth';
import lastDayOfMonth from 'date-fns/lastDayOfMonth';
import getMonth from 'date-fns/getMonth';
import setMonth from 'date-fns/setMonth';
import getYear from 'date-fns/getYear';
import setYear from 'date-fns/setYear';
import isSameMonth from 'date-fns/isSameMonth';
import isSameDay from 'date-fns/isSameDay';
import addDays from 'date-fns/addDays';
import subDays from 'date-fns/subDays';
import addWeeks from 'date-fns/addWeeks';
import subWeeks from 'date-fns/subWeeks';
import startOfMonth from 'date-fns/startOfMonth';
import startOfWeek from 'date-fns/startOfWeek';
import endOfWeek from 'date-fns/endOfWeek';
import isBefore from 'date-fns/isBefore';
import isAfter from 'date-fns/isAfter';
import isValid from 'date-fns/isValid';
import { findAncestor, randomString } from './airbnb-style-datepicker-helpers';
import {
    computed,
    defineComponent,
    nextTick,
    onBeforeUnmount,
    onMounted,
    PropType,
    reactive,
    Ref,
    ref,
    watch,
} from 'vue';
import viewportEvents from '@/core/responsive/viewport/viewport-events.service';

export default defineComponent({
    name: 'AirbnbStyleDatepicker',
    props: {
        triggerElementId: { type: String, default: '' },
        dateOne: { type: [String, Date], required: true },
        dateTwo: { type: [String, Date], default: '' },
        minDate: { type: [String, Date], default: '' },
        endDate: { type: [String, Date], default: '' },
        mode: { type: String, default: 'range' },
        offsetY: { type: Number, default: 0 },
        offsetX: { type: Number, default: 0 },
        monthsToShow: { type: Number, default: 2 },
        startOpen: { type: Boolean },
        inline: { type: Boolean },
        mobileHeader: { type: String, default: '' },
        disabledDates: { type: Array, default: () => [] },
        enabledDates: { type: Array, default: () => [] },
        customizedDates: { type: Array as PropType<any[]>, default: () => [] },
        showActionButtons: { type: Boolean, default: true },
        showShortcutsMenuTrigger: { type: Boolean, default: true },
        showMonthYearSelect: { type: Boolean, default: false },
        yearsForSelect: { type: Number, default: 10 },
        trigger: { type: Boolean, default: false },
        closeAfterSelect: { type: Boolean, default: false },
    },
    emits: {
        closed: () => true,
        apply: () => true,
        opened: () => true,
        cancelled: () => true,
        'previous-month': () => true,
        'next-month': () => true,
        'date-one-selected': (eventName, date) => true,
        'date-two-selected': (eventName, date) => true,
    },
    setup(props, { emit }) {
        let triggerElement;
        let _handleWindowClickEvent;
        let isSelectingDate1 = false;
        let startingDate: Date | string = '';
        let hoverDate = '';
        let initialDate1 = '';
        let initialDate2 = '';
        let triggerPosition = {} as any;
        let triggerWrapperPosition = {} as any;

        const keys = {
            arrowDown: 40,
            arrowUp: 38,
            arrowRight: 39,
            arrowLeft: 37,
            enter: 13,
            pgUp: 33,
            pgDn: 34,
            end: 35,
            home: 36,
            questionMark: 191,
            esc: 27,
        };
        const wrapperId = 'airbnb-style-datepicker-wrapper-' + randomString(5);
        const dateFormat = 'yyyy-MM-dd';

        const mobileWidth = 650;
        const isMobile: Ref<boolean> = ref(false);
        const alignRight: Ref<boolean> = ref(false);
        const root: Ref<HTMLElement | null> = ref(null);
        const dateTds: Ref<HTMLTableCellElement[]> = ref([]);
        const applyButton: Ref<HTMLElement | null> = ref(null);
        const keyboardShortcutMenuClose: Ref<HTMLElement | null> = ref(null);
        const width = ref(300);
        const viewportWidth: Ref<string> = ref('0px');
        const focusedDate: Ref<string | Date> = ref('');
        const selectedDate1 = ref('');
        const selectedDate2 = ref('');
        const dateLabelFormat = ref('dddd, MMMM d, yyyy');
        const monthNames = ref([
            'January',
            'February',
            'March',
            'April',
            'May',
            'June',
            'July',
            'August',
            'September',
            'October',
            'November',
            'December',
        ]);
        const ariaLabels = ref({
            chooseDate: date => date,
            chooseStartDate: date => `Choose ${date} as your start date.`,
            chooseEndDate: date => `Choose ${date} as your end date.`,
            selectedDate: date => `Selected. ${date}`,
            unavailableDate: date => `Not available. ${date}`,
            previousMonth: 'Move backward to switch to the previous month.',
            nextMonth: 'Move forward to switch to the next month.',
            closeDatepicker: 'Close calendar',
            openKeyboardShortcutsMenu: 'Open keyboard shortcuts menu.',
            closeKeyboardShortcutsMenu: 'Close keyboard shortcuts menu',
        });
        const showMonths = ref(2);
        const days = ref(['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']);
        const daysShort = ref(['Mon', 'Tue', 'Wed', 'Thur', 'Fri', 'Sat', 'Sun']);
        const months: Ref<any[]> = ref([]);
        const years: Ref<any[]> = ref([]);
        const showDatepicker = ref(false);
        const showKeyboardShortcutsMenu = ref(false);
        const keyboardShortcuts = ref([
            { symbol: '↵', label: 'Select the date in focus', symbolDescription: 'Enter key' },
            {
                symbol: '←/→',
                label: 'Move backward (left) and forward (right) by one day.',
                symbolDescription: 'Left or right arrow keys',
            },
            {
                symbol: '↑/↓',
                label: 'Move backward (up) and forward (down) by one week.',
                symbolDescription: 'Up or down arrow keys',
            },
            {
                symbol: 'PgUp/PgDn',
                label: 'Switch months.',
                symbolDescription: 'PageUp and PageDown keys',
            },
            {
                symbol: 'Home/End',
                label: 'Go to the first or last day of a week.',
                symbolDescription: 'Home or End keys',
            },
            { symbol: 'Esc', label: 'Close this panel', symbolDescription: 'Escape key' },
            { symbol: '?', label: 'Open this panel', symbolDescription: 'Question mark' },
        ]);
        const colors = reactive({
            selected: '#1A3D69',
            inRange: '#ced8e0',
            selectedText: '#fff',
            text: '#565a5c',
            inRangeBorder: '#f5f5f5',
            disabled: '#fff',
            hoveredInRange: '#ced8e0',
        });

        const texts = reactive({
            apply: 'Apply',
            cancel: 'Cancel',
            keyboardShortcuts: 'Keyboard Shortcuts',
        });

        const showFullscreen = computed(() => {
            return isMobile.value;
        });

        const wrapperClasses = computed(() => {
            return {
                'asd__wrapper--datepicker-open': showDatepicker.value,
                'asd__wrapper--full-screen': showFullscreen.value,
                'asd__wrapper--inline': props.inline,
            };
        });

        const wrapperStyles = computed(() => {
            return {
                position: props.inline ? 'static' : 'absolute',
                top: props.inline ? '0' : triggerPosition.height + props.offsetY + 'px',
                left: !alignRight.value
                    ? triggerPosition.left - triggerWrapperPosition.left + props.offsetX + 'px'
                    : '',
                right: alignRight.value
                    ? triggerWrapperPosition.right - triggerPosition.right + props.offsetX + 'px'
                    : '',
                width: width.value * showMonths.value + 'px',
                zIndex: props.inline ? '0' : '100',
            };
        });

        const innerStyles = computed(() => {
            return {
                'margin-left': showFullscreen.value ? '-' + viewportWidth.value : `-${width.value}px`,
            };
        });

        const keyboardShortcutsMenuStyles = computed(() => {
            return {
                left: showFullscreen.value ? viewportWidth.value : `${width.value}px`,
            };
        });

        const monthWidthStyles = computed(() => {
            return {
                width: showFullscreen.value ? viewportWidth.value : width.value + 'px',
            };
        });

        const mobileHeaderFallback = computed(() => {
            return props.mode === 'range' ? 'Select dates' : 'Select date';
        });

        const datesSelected = computed(() => {
            return !!(
                (selectedDate1.value && selectedDate1.value !== '') ||
                (selectedDate2.value && selectedDate2.value !== '')
            );
        });

        const allDatesSelected = computed(() => {
            return !!(
                selectedDate1.value &&
                selectedDate1.value !== '' &&
                selectedDate2.value &&
                selectedDate2.value !== ''
            );
        });

        const hasMinDate = computed(() => {
            return (props.minDate && props.minDate !== '');
        });

        const isRangeMode = computed(() => {
            return props.mode === 'range';
        });

        const isSingleMode = computed(() => {
            return props.mode === 'single';
        });

        const datepickerWidth = computed(() => {
            return width.value * showMonths.value;
        });

        const datePropsCompound = computed(() => {
            // used to watch for changes in props, and update GUI accordingly
            return props.dateOne.toString() + props.dateTwo.toString();
        });

        const isDateTwoBeforeDateOne = computed(() => {
            if (!props.dateTwo) {
                return false;
            }
            return isBefore(new Date(props.dateTwo), new Date(props.dateOne));
        });

        const visibleMonths = computed(() => {
            const firstMonthArray: any[] = months.value.filter((m, index) => index > 0);
            const numberOfMonthsArray: any[] = [];
            for (let i = 0; i < showMonths.value; i++) {
                numberOfMonthsArray.push(i);
            }

            return numberOfMonthsArray.map((_, index) => firstMonthArray[index].firstDateOfMonth);
        });

        const formatAndAddMonths = (date) => {
            return format(new Date(addMonths(new Date(date), 1)), dateFormat);
        };

        const isDateDisabled = (date) => {
            if (props.enabledDates.length > 0) {
                return props.enabledDates.indexOf(date) === -1;
            } else {
                return props.disabledDates.indexOf(date) > -1;
            }
        };

        const isBeforeMinDate = (date) => {
            if (!props.minDate) {
                return false;
            }
            return isBefore(new Date(date), new Date(props.minDate));
        };

        const isAfterEndDate = (date) => {
            if (!props.endDate) {
                return false;
            }
            return isAfter(new Date(date), new Date(props.endDate));
        };

        const isInRange = (date) => {
            if (!allDatesSelected.value || isSingleMode.value) {
                return false;
            }

            return (
                (isAfter(new Date(date), new Date(selectedDate1.value)) && isBefore(new Date(date), new Date(selectedDate2.value))) ||
                (isAfter(new Date(date), new Date(selectedDate1.value)) &&
                    isBefore(new Date(date), new Date(hoverDate)) &&
                    !allDatesSelected.value)
            );
        };

        const isDisabled = (date) => {
            return isDateDisabled(date) || isBeforeMinDate(date) || isAfterEndDate(date);
        };

        const isSelected = (date) => {
            if (!date) {
                return;
            }
            return selectedDate1.value === date || selectedDate2.value === date;
        };

        const isHoveredInRange = (date) => {
            if (isSingleMode.value || allDatesSelected.value) {
                return false;
            }

            return (
                (isAfter(new Date(date), new Date(selectedDate1.value)) && isBefore(new Date(date), new Date(hoverDate))) ||
                (isAfter(new Date(date), new Date(hoverDate)) && isBefore(new Date(date), new Date(selectedDate1.value)))
            );
        };

        const getDayStyles = (date) => {
            const dateIsSelected = isSelected(date);
            const dateIsInRange = isInRange(date);
            const dateIsDisabled = isDisabled(date);
            const dateIsHoveredInRange = isHoveredInRange(date);

            const styles = {
                width: (width.value - 30) / 7 + 'px',
                background: dateIsSelected
                    ? colors.selected
                    : dateIsHoveredInRange
                        ? colors.hoveredInRange
                        : dateIsInRange
                            ? colors.inRange
                            : '',
                color: dateIsSelected
                    ? colors.selectedText
                    : dateIsInRange || dateIsHoveredInRange
                        ? colors.selectedText
                        : colors.text,
                border: dateIsSelected
                    ? '1px double ' + colors.selected
                    : (dateIsInRange && allDatesSelected.value) || dateIsHoveredInRange
                        ? '1px double ' + colors.inRangeBorder
                        : '',
            };

            if (dateIsDisabled) {
                styles.background = colors.disabled;
            }
            return styles;
        };

        const getAriaLabelForDate = (date) => {
            const dateLabel = format(new Date(date), dateLabelFormat.value);

            if (isDisabled(date)) {
                return ariaLabels.value.unavailableDate(dateLabel);
            }

            if (isSelected(date)) {
                return ariaLabels.value.selectedDate(dateLabel);
            }

            if (isRangeMode.value) {
                if (isSelectingDate1) {
                    return ariaLabels.value.chooseStartDate(dateLabel);
                } else {
                    return ariaLabels.value.chooseEndDate(dateLabel);
                }
            } else {
                return ariaLabels.value.chooseDate(dateLabel);
            }
        };

        const closeDatepicker = () => {
            if (props.inline) {
                return;
            }
            showDatepicker.value = false;
            showKeyboardShortcutsMenu.value = false;
            triggerElement.classList.remove('datepicker-open');
            emit('closed');
        };

        const handleClickOutside = (event) => {
            if (event.target.id === props.triggerElementId || !showDatepicker.value || props.inline) {
                return;
            }

            closeDatepicker();
        };

        const shouldHandleInput = (event: any, key: any) => {
            return (
                event.keyCode === key && (!event.shiftKey || event.keyCode === 191) && showDatepicker.value
            );
        };

        const getWeeks = (date) => {
            const weekDayNotInMonth: any = { dayNumber: 0 };
            const daysInMonth = getDaysInMonth(new Date(date));
            const year = format(new Date(date), 'yyyy');
            const month = format(new Date(date), 'MM');
            const firstDayInWeek = parseInt(format(new Date(date), 'E'));

            const weeks: any[] = [];
            let week: any[] = [];

            // add empty days to get first day in correct position
            for (let s = 1; s < firstDayInWeek; s++) {
                week.push(weekDayNotInMonth);
            }

            for (let d = 0; d < daysInMonth; d++) {
                const isLastDayInMonth = d >= daysInMonth - 1;
                const dayNumber = d + 1;
                const dayNumberFull = dayNumber < 10 ? '0' + dayNumber : dayNumber;
                week.push({
                    dayNumber,
                    dayNumberFull: dayNumberFull,
                    fullDate: year + '-' + month + '-' + dayNumberFull,
                });

                if (week.length === 7) {
                    weeks.push(week);
                    week = [];
                } else if (isLastDayInMonth) {
                    for (let i = 0; i < 7 - week.length; i++) {
                        week.push(weekDayNotInMonth);
                    }
                    weeks.push(week);
                    week = [];
                }
            }
            return weeks;
        };

        const getMonthObj = (date) => {
            const firstDateOfMonth = format(new Date(date), 'yyyy-MM-01');
            const year = format(new Date(date), 'yyyy');
            const monthNumber = parseInt(format(new Date(date), 'M'));
            const monthName = monthNames.value[monthNumber - 1];

            return {
                year,
                firstDateOfMonth,
                monthName,
                monthNumber,
                weeks: getWeeks(firstDateOfMonth),
            };
        };

        const generateYears = () => {
            if (!props.showMonthYearSelect) {
                return;
            }

            years.value = [];
            const currentYear = getYear(new Date(startingDate));
            const startYear = props.minDate ? getYear(new Date(props.minDate)) : currentYear - props.yearsForSelect;
            const endYear = props.endDate ? getYear(new Date(props.endDate)) : currentYear + props.yearsForSelect;

            for (let year = startYear; year <= endYear; year++) {
                years.value.push(year.toString());
            }
        };

        const generateMonths = () => {
            months.value = [];
            let currentMonth = startingDate;
            for (let i = 0; i < showMonths.value + 2; i++) {
                months.value.push(getMonthObj(currentMonth));
                currentMonth = formatAndAddMonths(currentMonth);
            }
        };

        const selectDate = (date) => {
            if (isBeforeMinDate(date) || isAfterEndDate(date) || isDateDisabled(date)) {
                return;
            }

            if (props.mode === 'single') {
                selectedDate1.value = date;
                closeDatepicker();
                return;
            }

            if (isSelectingDate1 || isBefore(new Date(date), new Date(selectedDate1.value))) {
                selectedDate1.value = date;
                isSelectingDate1 = false;

                if (isBefore(new Date(selectedDate2.value), new Date(date))) {
                    selectedDate2.value = '';
                }
            } else {
                selectedDate2.value = date;
                isSelectingDate1 = true;

                if (isAfter(new Date(selectedDate1.value), new Date(date))) {
                    selectedDate1.value = '';
                } else if (props.showActionButtons && applyButton.value) {
                    // if user has selected both dates, focus the apply button for accessibility
                    applyButton.value.focus();
                }

                if (allDatesSelected.value && props.closeAfterSelect) {
                    closeDatepicker();
                }
            }
        };

        const setDateFromText = (value) => {
            if (!value || value.length < 10) {
                return;
            }
            // make sure format is either 'yyyy-MM-dd' or 'dd.MM.yyyy'
            const isFormatYearFirst = value.match(
                /^(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|1[0-9]|2[0-9]|3[0-1])$/,
            );
            const isFormatDayFirst = value.match(
                /^(0[1-9]|1[0-9]|2[0-9]|3[0-1])[.](0[1-9]|1[0-2])[.](\d{4})$/,
            );

            if (!isFormatYearFirst && !isFormatDayFirst) {
                return;
            }

            if (isFormatDayFirst) {
                // convert to YYYY-MM-DD
                value = `${value.substring(6, 10)}-${value.substring(3, 5)}-${value.substring(0, 2)}`;
            }

            const valueAsDateObject = new Date(value);

            if (!isValid(valueAsDateObject)) {
                return;
            }

            const formattedDate = format(new Date(valueAsDateObject), dateFormat);

            if (
                isDateDisabled(formattedDate) ||
                isBeforeMinDate(formattedDate) ||
                isAfterEndDate(formattedDate)
            ) {
                return;
            }
            startingDate = subMonths(new Date(formattedDate), 1);
            generateMonths();
            generateYears();
            selectDate(formattedDate);
        };

        const handleTriggerInput = (event) => {
            if (props.mode === 'single') {
                setDateFromText(event.target.value);
            }
        };

        const trapKeyboardInput = (event) => {
            // prevent keys that are used as keyboard shortcuts from propagating out of this element
            // except for the enter key, which is needed to activate buttons
            const shortcutKeyCodes = Object.keys(keys).map(key => keys[key]);
            shortcutKeyCodes.splice(shortcutKeyCodes.indexOf(13), 1);
            const shouldPreventDefault = shortcutKeyCodes.indexOf(event.keyCode) > -1;
            if (shouldPreventDefault) {
                event.preventDefault();
            }
        };

        const subtractMonths = (date) => {
            const dateOneMonthAgo = new Date(subMonths(new Date(date), 1));
            return format(dateOneMonthAgo, dateFormat);
        };

        const isDateVisible = (date) => {
            if (!date) {
                return false;
            }
            const start = subDays(new Date(visibleMonths.value[0]), 1);
            const end = addDays(new Date(lastDayOfMonth(new Date(visibleMonths.value[props.monthsToShow - 1]))), 1);
            return isAfter(new Date(date), start) && isBefore(new Date(date), end);
        };

        const resetFocusedDate = (setToFirst) => {
            if (focusedDate.value && !isDateVisible(focusedDate.value)) {
                const visibleMonthIdx = setToFirst ? 0 : visibleMonths.value.length - 1;
                const targetMonth = visibleMonths.value[visibleMonthIdx];
                const monthIdx = getMonth(new Date(targetMonth));
                const year = getYear(new Date(targetMonth));
                const newFocusedDate = setYear(setMonth(new Date(focusedDate.value), monthIdx), year);
                focusedDate.value = format(new Date(newFocusedDate), dateFormat);
            }
        };

        const previousMonth = () => {
            startingDate = subtractMonths(months.value[0].firstDateOfMonth);

            months.value.unshift(getMonthObj(startingDate));
            months.value.splice(months.value.length - 1, 1);
            emit('previous-month', visibleMonths.value);
            resetFocusedDate(false);
        };

        const setFocusedDate = (date) => {
            const formattedDate = format(new Date(date), dateFormat);
            focusedDate.value = formattedDate;
            const dateElement = dateTds.value[formattedDate];
            // handle .focus() on ie11 by adding a short timeout
            if (dateElement && dateElement.length) {
                setTimeout(function() {
                    dateElement[0].focus();
                }, 10);
            }
        };

        const closeKeyboardShortcutsMenu = () => {
            showKeyboardShortcutsMenu.value = false;
            nextTick(() => setFocusedDate(focusedDate.value));
        };

        const nextMonth = () => {
            startingDate = formatAndAddMonths(months.value[months.value.length - 1].firstDateOfMonth);
            months.value.push(getMonthObj(startingDate));
            months.value.splice(0, 1);
            emit('next-month', visibleMonths.value);
            resetFocusedDate(true);
        };

        const openKeyboardShortcutsMenu = () => {
            showKeyboardShortcutsMenu.value = true;
            const shortcutMenuCloseBtn = keyboardShortcutMenuClose.value;
            nextTick(() => shortcutMenuCloseBtn?.focus());
        };

        const handleKeyboardInput = (event) => {
            if (shouldHandleInput(event, keys.esc)) {
                if (showKeyboardShortcutsMenu.value) {
                    closeKeyboardShortcutsMenu();
                } else {
                    closeDatepicker();
                }
            } else if (showKeyboardShortcutsMenu.value) {
                // if keyboard shortcutsMenu is open, then esc is the only key we want to have fire events
            } else if (shouldHandleInput(event, keys.arrowDown)) {
                const newDate = addWeeks(new Date(focusedDate.value), 1);
                const changeMonths = !isSameMonth(newDate, new Date(focusedDate.value));
                setFocusedDate(newDate);
                if (changeMonths) {
                    nextMonth();
                }
            } else if (shouldHandleInput(event, keys.arrowUp)) {
                const newDate = subWeeks(new Date(focusedDate.value), 1);
                const changeMonths = !isSameMonth(newDate, new Date(focusedDate.value));
                setFocusedDate(newDate);
                if (changeMonths) {
                    previousMonth();
                }
            } else if (shouldHandleInput(event, keys.arrowRight)) {
                const newDate = addDays(new Date(focusedDate.value), 1);
                const changeMonths = !isSameMonth(newDate, new Date(focusedDate.value));
                setFocusedDate(newDate);
                if (changeMonths) {
                    nextMonth();
                }
            } else if (shouldHandleInput(event, keys.arrowLeft)) {
                const newDate = subDays(new Date(focusedDate.value), 1);
                const changeMonths = !isSameMonth(newDate, new Date(focusedDate.value));
                setFocusedDate(newDate);
                if (changeMonths) {
                    previousMonth();
                }
            } else if (shouldHandleInput(event, keys.enter)) {
                // on enter key, only select the date if a date is currently in focus
                const target = event.target;
                if (!showKeyboardShortcutsMenu.value && target && target.tagName === 'TD') {
                    selectDate(focusedDate.value);
                }
            } else if (shouldHandleInput(event, keys.pgUp)) {
                setFocusedDate(subMonths(new Date(focusedDate.value), 1));
                previousMonth();
            } else if (shouldHandleInput(event, keys.pgDn)) {
                setFocusedDate(addMonths(new Date(focusedDate.value), 1));
                nextMonth();
            } else if (shouldHandleInput(event, keys.home)) {
                const newDate = startOfWeek(new Date(focusedDate.value), {
                    weekStartsOn: 1,
                });
                const changeMonths = !isSameMonth(newDate, new Date(focusedDate.value));
                setFocusedDate(newDate);
                if (changeMonths) {
                    previousMonth();
                }
            } else if (shouldHandleInput(event, keys.end)) {
                const newDate = endOfWeek(new Date(focusedDate.value), {
                    weekStartsOn: 1,
                });
                const changeMonths = !isSameMonth(newDate, new Date(focusedDate.value));
                setFocusedDate(newDate);
                if (changeMonths) {
                    nextMonth();
                }
            } else if (shouldHandleInput(event, keys.questionMark)) {
                openKeyboardShortcutsMenu();
            }
        };

        const isMonthDisabled = (year, monthIndex) => {
            const monthDate = new Date(year, monthIndex);
            if (hasMinDate.value && isBefore(monthDate, startOfMonth(new Date(props.minDate)))) {
                return true;
            }
            return isAfterEndDate(monthDate);
        };

        const setStartDates = () => {
            let startDate = props.dateOne || new Date();
            if (hasMinDate.value && isBefore(new Date(startDate), new Date(props.minDate))) {
                startDate = props.minDate;
            }
            startingDate = subtractMonths(startDate);
            selectedDate1.value = props.dateOne.toString();
            selectedDate2.value = props.dateTwo.toString();
            focusedDate.value = startDate;
        };

        const setHoverDate = (date) => {
            hoverDate = date;
        };

        const isToday = (date) => {
            return format(new Date(), dateFormat) === date;
        };

        const isSameDate = (date1, date2) => {
            return isSameDay(new Date(date1), new Date(date2));
        };

        const customizedDateClass = (date) => {
            let customizedClasses = '';
            if (props.customizedDates.length > 0) {
                for (let i = 0; i < props.customizedDates.length; i++) {
                    if (props.customizedDates[i].dates.indexOf(date) > -1) {
                        customizedClasses += ` asd__day--${props.customizedDates[i].cssClass}`;
                    }
                }
            }
            return customizedClasses;
        };

        const positionDatepicker = () => {
            const triggerWrapperElement = findAncestor(triggerElement, '.datepicker-trigger');
            triggerPosition = triggerElement.getBoundingClientRect();
            if (triggerWrapperElement) {
                triggerWrapperPosition = triggerWrapperElement.getBoundingClientRect();
            } else {
                triggerWrapperPosition = { left: 0, right: 0 };
            }

            const tempViewportWidth = viewportEvents.viewport.width;
            viewportWidth.value = tempViewportWidth + 'px';
            isMobile.value = tempViewportWidth < mobileWidth;
            showMonths.value = isMobile.value ? 1 : props.monthsToShow ?? 2;

            nextTick(() => {
                const datepickerWrapper = root.value;
                if (!triggerElement || !datepickerWrapper) {
                    return;
                }

                setTimeout(() => {
                    const rightPosition =
                        triggerElement.getBoundingClientRect().left +
                        datepickerWrapper.getBoundingClientRect().width;
                    alignRight.value = rightPosition > tempViewportWidth;
                });
            });
        };

        const openDatepicker = () => {
            positionDatepicker();
            setStartDates();
            triggerElement.classList.add('datepicker-open');
            showDatepicker.value = true;
            initialDate1 = props.dateOne.toString();
            initialDate2 = props.dateTwo.toString();
            emit('opened');
            nextTick(() => {
                if (!props.inline) {
                    setFocusedDate(focusedDate.value);
                }
            });
        };

        const toggleDatepicker = () => {
            if (showDatepicker.value) {
                closeDatepicker();
            } else {
                openDatepicker();
            }
        };

        const updateMonth = (offset, year, event) => {
            const newMonth = event.target.value;
            const monthIdx = monthNames.value.indexOf(newMonth);
            const newDate = setYear(setMonth(new Date(startingDate), monthIdx), year);
            startingDate = subMonths(newDate, offset);
            generateMonths();
        };

        const updateYear = (offset, monthIdx, event) => {
            const newYear = event.target.value;
            const newDate = setYear(setMonth(new Date(startingDate), monthIdx), newYear);
            startingDate = subMonths(newDate, offset);
            generateMonths();
        };

        const closeDatepickerCancel = () => {
            if (showDatepicker.value) {
                selectedDate1.value = initialDate1;
                selectedDate2.value = initialDate2;
                emit('cancelled');
                closeDatepicker();
            }
        };

        const apply = () => {
            emit('apply');
            closeDatepicker();
        };

        const setupWatchers = () => {
            watch(selectedDate1, (newValue) => {
                const newDate = !newValue || newValue === '' ? '' : format(new Date(newValue), dateFormat);
                emit('date-one-selected', newDate);
            });

            watch(selectedDate2, (newValue) => {
                const newDate = !newValue || newValue === '' ? '' : format(new Date(newValue), dateFormat);
                emit('date-two-selected', newDate);
            });

            watch(() => props.mode, () => {
                setStartDates();
            });

            watch(() => props.minDate, () => {
                setStartDates();
                generateMonths();
                generateYears();
            });

            watch(() => props.endDate, () => {
                generateYears();
            });

            watch(() => props.trigger, (newValue) => {
                if (newValue) {
                    setTimeout(() => {
                        openDatepicker();
                    }, 0);
                }
            });

            watch(() => datePropsCompound, (newValue) => {
                if (props.dateOne !== selectedDate1.value) {
                    startingDate = props.dateOne;
                    setStartDates();
                    generateMonths();
                    generateYears();
                }
                if (isDateTwoBeforeDateOne.value) {
                    selectedDate2.value = '';
                    emit('date-two-selected', '');
                }
            });
        };

        const windowResizeHandler = () => {
            positionDatepicker();
            setStartDates();
        };

        onMounted(() => {
            viewportWidth.value = window.innerWidth + 'px';
            isMobile.value = window.innerWidth < mobileWidth;

            _handleWindowClickEvent = (event) => {
                if (event.target.id === props.triggerElementId) {
                    event.stopPropagation();
                    event.preventDefault();
                    toggleDatepicker();
                }
            };

            viewportEvents.setCallback(windowResizeHandler);

            triggerElement = document.getElementById(props.triggerElementId);

            setStartDates();
            generateMonths();
            generateYears();

            if (props.startOpen || props.inline) {
                openDatepicker();
            }

            if (root.value) {
                root.value.addEventListener('keyup', handleKeyboardInput);
                root.value.addEventListener('keydown', trapKeyboardInput);
            }

            if (triggerElement) {
                triggerElement.addEventListener('keyup', handleTriggerInput);
                triggerElement.addEventListener('click', _handleWindowClickEvent);
            }

            setupWatchers();
        });

        onBeforeUnmount(() => {
            viewportEvents.removeCallback(windowResizeHandler);
            window.removeEventListener('click', _handleWindowClickEvent);

            if (root.value) {
                root.value.removeEventListener('keyup', handleKeyboardInput);
                root.value.removeEventListener('keydown', trapKeyboardInput);
            }

            if (triggerElement) {
                triggerElement.removeEventListener('keyup', handleTriggerInput);
                triggerElement.removeEventListener('click', _handleWindowClickEvent);
            }
        });

        return {
            wrapperId,
            isSingleMode,
            isDateTwoBeforeDateOne,
            visibleMonths,
            datePropsCompound,
            datepickerWidth,
            dateFormat,
            dateLabelFormat,
            monthNames,
            ariaLabels,
            days,
            daysShort,
            showDatepicker,
            showKeyboardShortcutsMenu,
            colors,
            keyboardShortcuts,
            root,
            texts,
            showMonths,
            months,
            years,
            year: '',
            selectedDate1,
            selectedDate2,
            focusedDate,
            width,
            showFullscreen,
            wrapperClasses,
            wrapperStyles,
            innerStyles,
            keyboardShortcutsMenuStyles,
            monthWidthStyles,
            mobileHeaderFallback,
            datesSelected,
            allDatesSelected,
            hasMinDate,
            isRangeMode,
            applyButton,
            dateTds,
            keyboardShortcutMenuClose,
            getDayStyles,
            closeKeyboardShortcutsMenu,
            getAriaLabelForDate,
            handleClickOutside,
            shouldHandleInput,
            closeDatepicker,
            previousMonth,
            isMonthDisabled,
            isDateVisible,
            isHoveredInRange,
            nextMonth,
            isToday,
            customizedDateClass,
            updateMonth,
            updateYear,
            selectDate,
            closeDatepickerCancel,
            apply,
            isSameDate,
            setHoverDate,
            isDisabled,
            openKeyboardShortcutsMenu,
            isInRange,
        };
    },
});
